// @flow
import {type AxiosPromise, type AxiosResponse} from 'axios';
import {createActions, handleActions, type ActionType, type ReduxReducer} from 'redux-actions';
import {FULFILLED, PENDING, REJECTED} from 'redux-promise-middleware';

import {ENDPOINTS} from 'constants/endpoints';
import {HTTP_REQUEST_HEADER, HTTP_REQUEST_HEADER_CONTENT_TYPE_VALUE} from 'constants/api';
import {instanceGusaBackend} from 'libs/api';

import type {CreatedOrderItemType, CreatedOrderType, ParameterType} from './createdOrderType';

export const defaultState: CreatedOrderType = {
    status: PENDING,
    items: [],
    incrementId: 0,
    orderId: 0,
    commonMethod: 0,
    commonFile: '',
    isCommonMethodSelected: true,
    token: '',
    total: 0,
    totalDue: 0,
    isEditable: true,
};

/**
 * Info about last successfully saved order
 * @param {OrderDataType} state Current state
 * @param {ActionType<AxiosPromise>} action Received action
 * @returns {OrderDataType} last saved order info
 */
export const {
    createdOrder: {fetchOrder, sendPrescription, setParameter, createZendeskTicket},
} = createActions({
    CREATED_ORDER: {
        /**
         * Fetches the the latest order from backend
         * @param {string} order - created order
         * @param {string} token - created order token
         * @return {AxiosPromise} information about last order
         */
        FETCH_ORDER: (order: string, token: string): AxiosPromise =>
            instanceGusaBackend({
                headers: {
                    'Order-Token': token,
                },
                method: 'GET',
                url: `${ENDPOINTS.ORDER_GET}/${order}`,
            }),

        /**
         * Get faces images from backend
         * @param {string} orderItem - created order
         * @param {string} token - created order token
         * @param {number} orderId - order Number
         * @return {AxiosPromise} Faces images from backend
         */
        SEND_PRESCRIPTION: [
            (orderItem: CreatedOrderItemType, token: string, orderId: number): AxiosPromise => {
                const {method, file} = orderItem;

                return instanceGusaBackend({
                    method: 'PUT',
                    headers: {
                        [HTTP_REQUEST_HEADER.CONTENT_TYPE]:
                            HTTP_REQUEST_HEADER_CONTENT_TYPE_VALUE.APPLICATION_JSON,
                        'Order-Token': token,
                    },
                    url: ENDPOINTS.ORDER_SEND_PRESCRIPTION({orderId, itemId: orderItem.id}).replace(
                        /#/gi,
                        '%23',
                    ),
                    data: {
                        prescription: {sendBy: method, file, values: []},
                    },
                });
            },
            (orderItem: CreatedOrderItemType): number => orderItem.id,
        ],

        /**
         * Sets any parametr
         * @param {ParameterType} data param
         * @returns {ParameterType} - param
         */
        SET_PARAMETER: (data: ParameterType): ParameterType => data,

        /**
         * Creates zendesk ticket for order
         * @param {number} orderId - order
         * @returns {AxiosPromise} action
         */
        CREATE_ZENDESK_TICKET: (orderId: number): AxiosPromise =>
            instanceGusaBackend({
                method: 'GET',
                url: `${ENDPOINTS.ORDER_CREATE_ZENDESK_TICKET}/${orderId}`,
            }),
    },
});

export const createdOrder: ReduxReducer<
    CreatedOrderType,
    ActionType<AxiosResponse | number>,
> = handleActions(
    {
        /**
         * Set pending status for fetching created order
         * @param {CreatedOrderType} state - Current state
         * @return {CreatedOrderType} New state
         */
        [`${fetchOrder}_${PENDING}`]: (state: CreatedOrderType): CreatedOrderType => ({
            ...state,
            status: PENDING,
        }),

        /**
         * Fetch the latest order
         * @param {CreatedOrderType} state - Current state
         * @param {ActionType<fetchOrder>} action - Dispatched action
         * @return {CreatedOrderType} New state
         */
        [`${fetchOrder}_${FULFILLED}`]: (
            state: CreatedOrderType,
            {payload}: ActionType<AxiosResponse<fetchOrder>>,
        ): CreatedOrderType => ({
            ...state,
            ...payload.data,
            items: payload.data.items
                .filter((item: CreatedOrderItemType) => item.product.isEligibleForRx)
                .map((product: CreatedOrderItemType) => ({
                    ...product,
                    method: 0,
                    file: '',
                    status: '',
                })),
            status: FULFILLED,
        }),

        /**
         * Sets fulfilled status to item
         * @param {CreatedOrderType} state - Current state
         * @param {ActionType<sendPrescription>} action - Dispatched action
         * @return {CreatedOrderType} New state
         */
        [`${sendPrescription}_${FULFILLED}`]: (
            state: CreatedOrderType,
            {meta}: ActionType<typeof sendPrescription>,
        ): CreatedOrderType => ({
            ...state,
            items: state.items.map((product: CreatedOrderItemType): CreatedOrderItemType => {
                if (product.id === meta) {
                    return {...product, status: FULFILLED};
                }
                return product;
            }),
        }),

        /**
         * Sets rejected status to item status to item
         * @param {CreatedOrderType} state - Current state
         * @param {ActionType<sendPrescription>} action - Dispatched action
         * @return {CreatedOrderType} New state
         */
        [`${sendPrescription}_${REJECTED}`]: (
            state: CreatedOrderType,
            {meta}: ActionType<typeof sendPrescription>,
        ): CreatedOrderType => ({
            ...state,
            items: state.items.map((product: CreatedOrderItemType): CreatedOrderItemType => {
                if (product.id === meta) {
                    return {...product, status: REJECTED};
                }
                return product;
            }),
        }),

        /**
         * Sets rejected status to item status to item
         * @param {CreatedOrderType} state - Current state
         * @param {ActionType<sendPrescription>} action - Dispatched action
         * @return {CreatedOrderType} New state
         */
        [`${sendPrescription}_${PENDING}`]: (state: CreatedOrderType): CreatedOrderType => ({
            ...state,
            items: state.items.map((product: CreatedOrderItemType): CreatedOrderItemType => ({
                ...product,
                status: PENDING,
            })),
        }),

        /**
         * Sets order id after route to upload rx
         * @param {CreatedOrderType} state - Current state
         * @param {ActionType<setParameter>} action - Dispatched action
         * @return {CreatedOrderType} New state
         */
        [setParameter]: (
            state: CreatedOrderType,
            {payload}: ActionType<typeof setParameter>,
        ): CreatedOrderType => ({
            ...state,
            ...payload,
        }),
    },
    defaultState,
);
